
import { useState, useEffect, useMemo } from 'react'
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useEstimateGas,
  useGasPrice
} from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import {

  Save,
  Copy,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Wallet,
  RefreshCw,
  DollarSign
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Interface } from 'ethers'
import { NODE_REGISTRY_ADDRESSES, NODE_REGISTRY_ABI, CHAIN_CONFIG, NODE_EXAMPLES } from '@/lib/config'
import { validateNodeAddress, copyToClipboard, getBlockExplorerUrl, formatEther, formatGasPrice, parseMultiaddr } from '@/lib/utils'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { uriToMultiaddr } from "@multiformats/uri-to-multiaddr"
export function MyNodePage() {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const [nodeInput, setNodeInput] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const contractAddress = NODE_REGISTRY_ADDRESSES[chainId as keyof typeof NODE_REGISTRY_ADDRESSES]

  // Read current node address
  const {
    data: currentNode,
    isLoading: isLoadingCurrent,
    isError: isErrorCurrent,
    refetch: refetchCurrent
  } = useReadContract({
    address: contractAddress,
    abi: NODE_REGISTRY_ABI,
    functionName: 'nodeOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && !!contractAddress,
    },
  })
  const finalCurrentNode = useMemo(() => {
    if (!currentNode) return null
    return parseMultiaddr(currentNode)
  }, [currentNode])

  // Write contract hook
  const {
    writeContract,
    data: hash,
    isPending: isWritePending,
    error: writeError
  } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isConfirming,
    isSuccess: isConfirmed,
    error: confirmError
  } = useWaitForTransactionReceipt({
    hash,
  })

  // Gas estimation
  const { data: gasPrice } = useGasPrice()
  const { data: gasEstimate } = useEstimateGas({
    to: contractAddress,
    data: contractAddress && nodeInput ?
      // Encode function call for gas estimation
      (() => {
        try {
          const iface = new Interface(NODE_REGISTRY_ABI)
          return iface.encodeFunctionData('setNode', [nodeInput]) as `0x${string}`
        } catch {
          return undefined
        }
      })() : undefined,
    query: {
      enabled: !!contractAddress && !!nodeInput && validateNodeAddress(nodeInput).isValid,
    }
  })

  // Update input when current node changes
  useEffect(() => {
    if (nodeInput && !isEditing) {
      setNodeInput(nodeInput)
    }
  }, [nodeInput, isEditing])

  // Handle successful transaction
  useEffect(() => {
    if (isConfirmed) {
      toast.success('Node address updated successfully!')
      setIsEditing(false)
      refetchCurrent()
    }
  }, [isConfirmed, refetchCurrent])

  // Handle transaction error
  useEffect(() => {
    if (writeError) {
      toast.error(`Transaction failed: ${writeError.message}`)
    }
    if (confirmError) {
      toast.error(`Transaction confirmation failed: ${confirmError.message}`)
    }
  }, [writeError, confirmError])

  const handleSave = () => {
    if (!address || !contractAddress) return

    const validation = validateNodeAddress(nodeInput)
    if (!validation.isValid) {
      toast.error(validation.error || 'Invalid node address')
      return
    }
    writeContract({
      address: contractAddress,
      abi: NODE_REGISTRY_ABI,
      functionName: 'setNode',
      args: [uriToMultiaddr(nodeInput).toString()],
    })
  }

  const handleClear = () => {
    if (!address || !contractAddress) return

    writeContract({
      address: contractAddress,
      abi: NODE_REGISTRY_ABI,
      functionName: 'setNode',
      args: [''],
    })
  }

  const handleCopyNode = async () => {
    if (!finalCurrentNode) return
    try {
      await copyToClipboard(finalCurrentNode)
      toast.success('Node address copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy node address')
    }
  }

  const handleUseExample = (example: string) => {
    setNodeInput(example)
    setIsEditing(true)
  }

  const validation = validateNodeAddress(nodeInput)
  const hasChanges = nodeInput !== (currentNode || '')
  const isTransacting = isWritePending || isConfirming
  const chainInfo = CHAIN_CONFIG[chainId as keyof typeof CHAIN_CONFIG]

  if (!isConnected) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
            <Wallet size={48} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Connect Your Wallet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Connect your wallet to view and manage your node address on the registry.
            </p>
            <ConnectButton />
          </div>
        </div>
      </div>
    )
  }

  if (!contractAddress) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl p-6">
          <div className="flex items-center space-x-2">
            <AlertCircle size={20} className="text-yellow-500" />
            <div>
              <h3 className="text-yellow-800 dark:text-yellow-200 font-medium">
                Network Not Supported
              </h3>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                The registry contract is not deployed on {chainInfo?.name || 'this network'}.
                Please switch to Ethereum or Base network.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          My Node
        </h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Manage your node address on the registry. Other users can discover your node by searching for your wallet address.
        </p>
      </div>

      {/* Current Status */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Current Status
          </h3>
          {chainInfo && (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <span style={{ color: chainInfo.color }}>{chainInfo.icon}</span>
              <span>{chainInfo.name}</span>
            </div>
          )}
        </div>

        {isLoadingCurrent ? (
          <div className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <LoadingSpinner size="sm" />
            <span className="text-gray-600 dark:text-gray-400">
              Loading current node address...
            </span>
          </div>
        ) : isErrorCurrent ? (
          <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertCircle size={20} className="text-red-500" />
            <div>
              <div className="text-red-700 dark:text-red-300 font-medium">
                Error loading node address
              </div>
              <button
                onClick={() => refetchCurrent()}
                className="text-red-600 dark:text-red-400 text-sm hover:underline flex items-center space-x-1"
              >
                <RefreshCw size={14} />
                <span>Retry</span>
              </button>
            </div>
          </div>
        ) : currentNode && currentNode.length > 0 ? (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">

              Update Node Address              <CheckCircle2 size={20} className="text-green-500" />
              <span className="text-green-700 dark:text-green-300 font-medium">
                Node address registered
              </span>
            </div>
            <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
              <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                {parseMultiaddr(currentNode)}
              </code>
              <button
                onClick={handleCopyNode}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                title="Copy node address"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <AlertCircle size={20} className="text-gray-500" />
            <div>
              <div className="text-gray-700 dark:text-gray-300 font-medium">
                No node address set
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Set your node address below to make it discoverable by others.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Node Address Form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {currentNode && currentNode.length > 0 ? 'Update Node Address' : 'Set Node Address'}
        </h3>

        <div className="space-y-4">
          <div>
            <label htmlFor="node-input" className="block text-sm font-medium text-gray-700 mb-2">
              Node Address (URL or IP:port)
            </label>
            <input
              id="node-input"
              type="text"
              value={nodeInput}
              onChange={(e) => {
                setNodeInput(e.target.value)
                setIsEditing(true)
              }}
              placeholder="https://node.example.com or 192.168.1.100:8080"
              className={`text-black input-field ${!validation.isValid && nodeInput ? 'border-red-500 focus:ring-red-500' : ''
                }`}
              maxLength={64}
            />

            {/* Character Count */}
            <div className="flex justify-between items-center mt-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {nodeInput.length}/64 characters
              </div>
              {!validation.isValid && nodeInput && (
                <div className="text-sm text-red-500">
                  {validation.error}
                </div>
              )}
            </div>
          </div>


          {gasEstimate && gasPrice && validation.isValid && nodeInput ? (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-blue-700 dark:text-blue-300">
                <DollarSign size={16} />
                <span>
                  Estimated gas: {gasEstimate.toString()} units
                  ({formatGasPrice(gasPrice)}, ~{formatEther(gasEstimate * gasPrice)} ETH)
                </span>
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              disabled={!validation.isValid || !hasChanges || isTransacting}
              className="btn-primary flex items-center justify-center space-x-2 flex-1"
            >
              {isTransacting ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Save size={18} />
              )}
              <span>
                {isWritePending ? 'Confirming...' : isConfirming ? 'Processing...' : 'Save Node Address'}
              </span>
            </button>

            {currentNode && currentNode.length > 0 && (
              <button
                onClick={handleClear}
                disabled={isTransacting}
                className="btn-secondary flex items-center justify-center space-x-2"
              >
                <span>Clear Address</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Examples */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Example Node Addresses
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NODE_EXAMPLES.map((example, index) => (
            <button
              key={index}
              onClick={() => handleUseExample(example)}
              className="p-3 text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors duration-200"
            >
              <code className="text-sm text-gray-700 dark:text-gray-300">
                {example}
              </code>
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
          Click any example to use it as a template. Node addresses must be 64 characters or less.
        </p>
      </div>

      {/* Transaction Status */}
      {hash && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Transaction Status
          </h3>
          <div className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
              {hash}
            </code>
            <a
              href={getBlockExplorerUrl(chainId, hash)}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              title="View transaction"
            >
              <ExternalLink size={16} />
            </a>
          </div>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {isConfirming ? 'Waiting for confirmation...' :
              isConfirmed ? 'Transaction confirmed!' :
                'Transaction submitted'}
          </div>
        </div>
      )}
    </div>
  )
}
